import { computed, unref } from 'vue'
import { useHead, useRoute, useSeoMeta, useSiteConfig } from '#imports'
import type { MaybeRef } from 'vue'

const TWICPICS_DOMAIN = 'https://kudosales.twic.pics'

const toOgTwicUrl = (localUrl: string, dimensions = { width: 1200, height: 630 }) => {
  const clean = localUrl.startsWith('/') ? localUrl : `/${localUrl}`
  const separator = clean.includes('?') ? '&' : '?'
  return `${TWICPICS_DOMAIN}${clean}${separator}twic=v1/refit=${dimensions.width}x${dimensions.height}(15p)/quality=80`
}

type ContentPage = {
  title?: string
  description?: string
  image?: string
}

type ContentSeoFallback = {
  title?: MaybeRef<string>
  description?: MaybeRef<string>
  image?: MaybeRef<string>
}

export const useContentSeo = (
  page: MaybeRef<ContentPage | null | undefined>,
  fallback: ContentSeoFallback = {}
) => {
  const route = useRoute()
  const site = useSiteConfig()

  const fallbackTitle = computed(() => unref(fallback.title) || site.name || 'Kudo Sales')
  const fallbackDescription = computed(
    () => unref(fallback.description) || site.description || ''
  )
  const fallbackImage = computed(() => unref(fallback.image) || '')

  const resolvedTitle = computed(
    () => unref(page)?.title || fallbackTitle.value
  )
  const resolvedDescription = computed(
    () => unref(page)?.description || fallbackDescription.value
  )
  const resolvedImage = computed(
    () => unref(page)?.image || fallbackImage.value
  )

  const canonicalUrl = computed(() => {
    if (!site.url) return undefined
    return new URL(route.path, site.url).toString()
  })

  const ogImage = computed(() => {
    if (!resolvedImage.value) return undefined
    if (/^https?:\/\//.test(resolvedImage.value)) return resolvedImage.value
    return toOgTwicUrl(resolvedImage.value)
  })

  const absoluteImage = computed(() => {
    if (!ogImage.value) return undefined
    if (/^https?:\/\//.test(ogImage.value)) return ogImage.value
    if (!site.url) return ogImage.value
    return new URL(ogImage.value, site.url).toString()
  })

  useSeoMeta({
    title: resolvedTitle,
    description: resolvedDescription,
    ogTitle: resolvedTitle,
    ogDescription: resolvedDescription,
    ogImage: absoluteImage,
    ogUrl: canonicalUrl,
    twitterTitle: resolvedTitle,
    twitterDescription: resolvedDescription,
    twitterImage: absoluteImage,
    twitterCard: computed(() => (absoluteImage.value ? 'summary_large_image' : 'summary')),
  })

  useHead(() => ({
    link: canonicalUrl.value ? [{ rel: 'canonical', href: canonicalUrl.value }] : [],
  }))

  useHead(() => ({
    script: [
      {
        key: 'schema-webpage',
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: resolvedTitle.value,
          description: resolvedDescription.value || undefined,
          url: canonicalUrl.value,
        }),
      },
    ],
  }))
}
